import React, { useState, useEffect, memo } from 'react';
import { X, Maximize2, Minimize2, MessageSquare, WifiOff } from 'lucide-react';
import { useTranslation, type Language } from '../hooks/useTranslation';
import { ChatInterface } from './ChatInterface';
import type { PollingConfig, AttributionType, HeaderConfig } from '../types/chat';
import type { SharedChatState, SharedChatStateSetters } from '../hooks/useSharedChatState';

/**
 * Props for the ChatWindow component
 */
interface ChatWindowProps {
  /** The URL of the chat server */
  serverUrl: string;
  /** The agent ID to use for the chat */
  agentId: string;
  /** The customer ID for the chat session */
  customerId: string;
  /** Customer name for personalization */
  customerName?: string;
  /** Agent name for display purposes */
  agentName?: string;
  /** Authentication token for backend requests */
  authToken?: string;
  /** When true, block session creation until authToken is present */
  requireAuth?: boolean;
  /** Whether to automatically scroll to new messages */
  scrollToNewMessage?: boolean;
  /** 
   * Smart auto-scroll behavior (default: true)
   * When enabled, only scrolls to bottom when user is at/near the bottom.
   * Users who have scrolled up won't be interrupted by new messages.
   */
  respectUserScroll?: boolean;
  /** Language for the interface */
  language?: Language;
  /** Shared session ID from parent */
  sessionId?: string | null;
  /** Whether welcome message has been shown */
  hasShownWelcome?: boolean;
  /** Callback when a session is created */
  onSessionCreated?: (sessionId: string) => void;
  /** Callback when welcome message is shown */
  onWelcomeShown?: () => void;
  /** Callback when chat is closed */
  onClose?: () => void;
  /** Callback when expand to full screen is requested */
  onExpand?: () => void;
  /** Callback when contract from full screen is requested */
  onContract?: () => void;
  /** Callback when sessions toggle button is clicked */
  onToggleSessions?: () => void;
  /** Layout mode for the chatbot */
  layoutMode?: 'popup' | 'fullscreen';
  /** Custom polling intervals for adaptive polling (optional) */
  pollingConfig?: PollingConfig;
  /** Attribution type: 'none' | 'enter1nine' (default: 'none') */
  attribution?: AttributionType;
  /** Welcome messages per language to send when creating a new session (optional) */
  welcomeMessages?: Record<'da' | 'en', string>;
  /** Header configuration for customizing buttons and appearance */
  headerConfig?: HeaderConfig;
  /** Custom CSS class for the window container */
  className?: string;
  /** External input message value (for controlled state - persists across mode changes) */
  inputMessage?: string;
  /** Callback for input message changes (for controlled state) */
  onInputMessageChange?: (message: string) => void;
  /** Whether debug mode is enabled (shows draft, logs, etc.) */
  debugMode?: boolean;
  /** Records scroll/bubble/cursor layout timeline for jump debugging */
  layoutDebug?: boolean;
  /** Shared chat state from parent (for state persistence across mode switches) */
  sharedState?: SharedChatState;
  /** Setters for shared chat state */
  sharedStateSetters?: SharedChatStateSetters;
}

interface ChatWindowHeaderToolbarProps {
  layoutMode: 'popup' | 'fullscreen';
  showExpandButton: boolean;
  showContractButton: boolean;
  showCloseButton: boolean;
  showSessionsToggleButton: boolean;
  expandTitle: string;
  closeTitle: string;
  onExpand?: () => void;
  onContract?: () => void;
  onClose?: () => void;
  onToggleSessions?: () => void;
}

const ChatWindowHeaderToolbar = memo(function ChatWindowHeaderToolbar({
  layoutMode,
  showExpandButton,
  showContractButton,
  showCloseButton,
  showSessionsToggleButton,
  expandTitle,
  closeTitle,
  onExpand,
  onContract,
  onClose,
  onToggleSessions,
}: ChatWindowHeaderToolbarProps) {
  return (
    <div className="chatbot-header-buttons flex items-center space-x-2 relative z-10 flex-shrink-0">
      {layoutMode === 'fullscreen' && onToggleSessions && showSessionsToggleButton && (
        <button
          onClick={onToggleSessions}
          className="chatbot-sessions-toggle-button hidden md:inline-flex items-center justify-center w-10 h-10 text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20 rounded-md transition-all duration-200 border border-primary-foreground/50"
          title="Toggle Sessions"
        >
          <MessageSquare size={18} />
        </button>
      )}
      {layoutMode === 'popup' && onExpand && showExpandButton && (
        <button
          onClick={onExpand}
          className="chatbot-expand-button inline-flex items-center justify-center w-10 h-10 text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20 rounded-md transition-all duration-200 border border-primary-foreground/50"
          title={expandTitle}
        >
          <Maximize2 size={18} />
        </button>
      )}
      {layoutMode === 'fullscreen' && onContract && showContractButton && (
        <button
          onClick={onContract}
          className="chatbot-minimize-button hidden md:inline-flex items-center justify-center w-10 h-10 text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20 rounded-md transition-all duration-200 border border-primary-foreground/50"
          title="Exit Full Screen"
        >
          <Minimize2 size={18} />
        </button>
      )}
      {onClose && showCloseButton && (
        <button
          onClick={onClose}
          className={`chatbot-close-button w-10 h-10 text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20 rounded-md transition-all duration-200 border border-primary-foreground/50 ${layoutMode === 'fullscreen' ? 'hidden md:inline-flex' : 'inline-flex'
            } items-center justify-center`}
          title={closeTitle}
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
});

/**
 * Chat window component that provides a complete chat interface
 * using the shared ChatInterface component with a styled header.
 */
export const ChatWindow: React.FC<ChatWindowProps> = ({
  serverUrl,
  agentId,
  customerId,
  customerName,
  agentName,
  authToken,
  requireAuth = false,
  scrollToNewMessage = true,
  respectUserScroll = true,
  language = 'en',
  sessionId,
  hasShownWelcome,
  onSessionCreated,
  onWelcomeShown,
  onClose,
  onExpand,
  onContract,
  onToggleSessions,
  layoutMode = 'popup',
  pollingConfig,
  attribution = 'enter1nine',
  welcomeMessages,
  headerConfig,
  className,
  inputMessage,
  onInputMessageChange,
  debugMode = false,
  layoutDebug = false,
  sharedState,
  sharedStateSetters
}) => {
  const { t } = useTranslation(language);
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const [isBrowserOffline, setIsBrowserOffline] = useState<boolean>(false);
  const [showDisconnectedBanner, setShowDisconnectedBanner] = useState<boolean>(false);

  // Use translated agent name if not provided
  const displayAgentName = agentName || t('agent.defaultName');

  // Header configuration with defaults
  const showExpandButton = headerConfig?.showExpandButton !== false;
  const showContractButton = headerConfig?.showContractButton !== false;
  const showCloseButton = headerConfig?.showCloseButton !== false;
  const showSessionsToggleButton = headerConfig?.showSessionsToggleButton !== false;
  const showConnectionStatus = headerConfig?.showConnectionStatus !== false;
  const showAgentAvatar = headerConfig?.showAgentAvatar !== false;

  /**
   * Handles connection status changes from ChatInterface
   */
  const handleConnectionChange = (connected: boolean, browserOffline?: boolean): void => {
    setIsConnected(connected);
    setIsBrowserOffline(browserOffline ?? false);
    // Only show disconnected banner when browser is offline (not for permanent errors)
    if (browserOffline) {
      setShowDisconnectedBanner(true);
    } else {
      setShowDisconnectedBanner(false);
    }
  };

  // Auto-hide banner after reconnection with a delay
  useEffect(() => {
    if (isConnected && showDisconnectedBanner) {
      const timer = setTimeout(() => {
        setShowDisconnectedBanner(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isConnected, showDisconnectedBanner]);

  /**
   * Handles closing the chatbot
   */
  const handleClose = (): void => {
    onClose?.();
  };

  // Support custom header render prop
  if (headerConfig?.renderHeader) {
    return (
      <div className={`chatbot-window flex flex-col h-full bg-card overflow-hidden border border-border ${layoutMode === 'popup'
        ? 'rounded-lg shadow-2xl'
        : 'shadow-2xl'
        } ${className || ''}`}>
        {/* Custom Header */}
        {headerConfig.renderHeader({
          agentName: displayAgentName,
          isConnected,
          layoutMode,
          onClose: showCloseButton ? handleClose : undefined,
          onExpand: showExpandButton ? onExpand : undefined,
          onContract: showContractButton ? onContract : undefined,
          onToggleSessions: showSessionsToggleButton ? onToggleSessions : undefined,
        })}

        {/* Connection Status Banner - Only show when browser is offline */}
        {isBrowserOffline && (
          <div 
            className={`chatbot-connection-banner overflow-hidden transition-all duration-500 ease-in-out ${
              showDisconnectedBanner ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-red-500/90 text-white">
              <WifiOff size={16} />
              <span>{t('status.connectionLost')}</span>
            </div>
          </div>
        )}

        {/* Chat Interface */}
        <div className="flex-1 flex flex-col min-h-0">
          <ChatInterface
            serverUrl={serverUrl}
            agentId={agentId}
            customerId={customerId}
            customerName={customerName}
            agentName={displayAgentName}
            authToken={authToken}
            requireAuth={requireAuth}
            scrollToNewMessage={scrollToNewMessage}
            respectUserScroll={respectUserScroll}
            language={language}
            sessionId={sessionId}
            hasShownWelcome={hasShownWelcome}
            onSessionCreated={onSessionCreated}
            onWelcomeShown={onWelcomeShown}
            pollingConfig={pollingConfig}
            attribution={attribution}
            welcomeMessages={welcomeMessages}
            onConnectionChange={handleConnectionChange}
            inputMessage={inputMessage}
            onInputMessageChange={onInputMessageChange}
            debugMode={debugMode}
            layoutDebug={layoutDebug}
            layoutMode={layoutMode}
            sharedState={sharedState}
            sharedStateSetters={sharedStateSetters}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`chatbot-window flex flex-col h-full bg-card overflow-hidden border border-border ${layoutMode === 'popup'
      ? 'rounded-lg shadow-2xl'
      : 'shadow-2xl'
      } ${className || ''}`}>
      {/* Chat Header */}
      <div className="chatbot-header bg-primary p-4 sm:p-6 flex items-center justify-between relative overflow-hidden">

        <div className="flex items-center space-x-3 sm:space-x-4 relative z-10 min-w-0">
          {showAgentAvatar && (
            <div className="chatbot-avatar w-10 h-10 sm:w-12 sm:h-12 bg-primary-foreground rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="chatbot-avatar-text text-primary font-bold text-lg sm:text-xl">{displayAgentName.charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="chatbot-header-title text-primary-foreground font-bold text-lg sm:text-xl truncate">{displayAgentName}</h3>
            {showConnectionStatus && (
              <div className="chatbot-connection-status flex items-center gap-1.5 mt-0.5">
                <div 
                  className={`chatbot-connection-status-dot w-2 h-2 rounded-full transition-colors duration-300 ${
                    isConnected 
                      ? 'bg-green-400 animate-pulse' 
                      : 'bg-red-400'
                  }`} 
                />
                <span className="chatbot-connection-status-text text-primary-foreground/80 text-xs font-medium">
                  {isConnected ? t('status.connected') : t('status.disconnected')}
                </span>
              </div>
            )}
          </div>
        </div>

        <ChatWindowHeaderToolbar
          layoutMode={layoutMode}
          showExpandButton={showExpandButton}
          showContractButton={showContractButton}
          showCloseButton={showCloseButton}
          showSessionsToggleButton={showSessionsToggleButton}
          expandTitle={t('common.expand')}
          closeTitle={t('common.close')}
          onExpand={onExpand}
          onContract={onContract}
          onClose={showCloseButton ? handleClose : undefined}
          onToggleSessions={onToggleSessions}
        />
      </div>

      {/* Connection Status Banner - Only show when browser is offline */}
      {isBrowserOffline && (
        <div 
          className={`chatbot-connection-banner overflow-hidden transition-all duration-500 ease-in-out ${
            showDisconnectedBanner ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-red-500/90 text-white">
            <WifiOff size={16} />
            <span>{t('status.connectionLost')}</span>
          </div>
        </div>
      )}

      {/* Chat Interface */}
      <div className="flex-1 flex flex-col min-h-0">
        <ChatInterface
          serverUrl={serverUrl}
          agentId={agentId}
          customerId={customerId}
          customerName={customerName}
          agentName={displayAgentName}
          authToken={authToken}
          requireAuth={requireAuth}
          scrollToNewMessage={scrollToNewMessage}
          respectUserScroll={respectUserScroll}
          language={language}
          sessionId={sessionId}
          hasShownWelcome={hasShownWelcome}
          onSessionCreated={onSessionCreated}
          onWelcomeShown={onWelcomeShown}
          pollingConfig={pollingConfig}
          attribution={attribution}
          welcomeMessages={welcomeMessages}
          onConnectionChange={handleConnectionChange}
          inputMessage={inputMessage}
          onInputMessageChange={onInputMessageChange}
          debugMode={debugMode}
          layoutDebug={layoutDebug}
          layoutMode={layoutMode}
          sharedState={sharedState}
          sharedStateSetters={sharedStateSetters}
        />
      </div>
    </div>
  );
};
